import React, { useState, useEffect } from "react";
import { 
    Box, Typography, Button, Paper, Table, TableBody, TableCell, 
    TableContainer, TableHead, TableRow, Chip, IconButton, 
    TablePagination, Stack, Dialog, DialogTitle, DialogContent,
    DialogActions, Divider, Alert, TextField
} from "@mui/material";
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { commonRunQuery } from "../../util/API/loans";
import { PortfolioReviewApplication } from "../../util/API/portfolio";
// Status codes from API: 1 = Approved, 2 = Rejected, 3 = Pending
const STATUS_MAP = {
    '1': { label: 'APPROVED', color: 'success' },
    '2': { label: 'REJECTED', color: 'error'   },
    '3': { label: 'PENDING',  color: 'warning'  },
};
const userId = JSON.parse(sessionStorage.getItem("user"))?.userid || "";
function ApplicationsManager({ onBack }) {
    const [applications, setApplications] = useState([]);
    const [loading, setLoading]           = useState(true);
    const [error, setError]               = useState('');
    const [selectedApp, setSelectedApp]   = useState(null);

    // Notes/Action Modal state
    const [actionModal, setActionModal]   = useState(null); // { app, type: 'accept' | 'reject' }
    const [actionNote, setActionNote]     = useState('');
    const [actionLoading, setActionLoading] = useState(false);

    // Pagination
    const [page, setPage]               = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(5);

    // Summary counts
    const totalCount    = applications.length;
    const pendingCount  = applications.filter(a => a.status === '3').length;
    const approvedCount = applications.filter(a => a.status === '1').length;
    const rejectedCount = applications.filter(a => a.status === '2').length;

    useEffect(() => {
        getApplications();
    }, []);

    const getApplications = async () => {
        setLoading(true);
        setError('');
        try {
            const response = await commonRunQuery(365, "");
            console.log("Applications Response ============", JSON.stringify(response));

            const records = response?.data?.body?.record;
            if (records) {
                const recordsArray = Array.isArray(records) ? records : [records];
                const mapped = recordsArray.map((item) => {
                    const fa = item.fa;
                    const f  = item.f;
                    const u  = item.u;
                    return {
                        applicationId:       fa.application_id,
                        requestedCommitment: Number(fa.requested_commitment),
                        status:              fa.status,
                        appliedAt:           fa.applied_at,
                        notes:               fa.notes?.replace(/&#039;/g, "'") || '—',
                        fundId:              f.fund_id,
                        fundName:            f.fund_name,
                        fundVisibility:      f.fund_visibility,
                        investorId:          u.investor_id,
                        investorName:        `${u.firstName || ''} ${u.lastName || ''}`.trim() || u.otherNames,
                    };
                });
                setApplications(mapped);
            }
        } catch (err) {
            console.error(err);
            setError('Failed to load applications. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    // Opens the notes modal for accept or reject
    const openActionModal = (app, type) => {
        setActionModal({ app, type });
        setActionNote('');
        setSelectedApp(null); // close detail dialog if open
    };

    // Confirms the action with note
  const handleConfirmAction = async () => {
    if (!actionNote.trim()) return;

    const newStatus = actionModal.type === 'accept' ? '1' : '2';
    setActionLoading(true);

    try {
        const response = await PortfolioReviewApplication(
            "",
            userId,
            actionModal.app.applicationId,
            newStatus,
            actionNote
        );

        console.log("PortfolioReviewApplication response:", JSON.stringify(response));

        const callStatus = response?.data?.header?.callStatus 
                        || response?.payload?.callStatus;

        if (callStatus !== '1' && callStatus !== 1) {
            console.error("Review action failed:", response);
            return;
        }

        // Update UI on success
        setApplications(prev =>
            prev.map(app =>
                app.applicationId === actionModal.app.applicationId
                    ? { ...app, status: newStatus, notes: actionNote }
                    : app
            )
        );

        setActionModal(null);
        setActionNote('');
    } catch (err) {
        console.error(err);
    } finally {
        setActionLoading(false);
    }
};

    const handleCloseActionModal = () => {
        setActionModal(null);
        setActionNote('');
    };

    const formatCurrency = (value) =>
        `$${Number(value).toLocaleString('en-US', { minimumFractionDigits: 2 })}`;

    const formatDate = (dateStr) =>
        new Date(dateStr).toLocaleDateString('en-US', { 
            year: 'numeric', month: 'short', day: 'numeric' 
        });

    // Pagination handlers
    const handleChangePage = (event, newPage) => setPage(newPage);
    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    const visibleApplications = applications.slice(
        page * rowsPerPage,
        page * rowsPerPage + rowsPerPage
    );

    return (
        <Box sx={{ p: 3, maxWidth: '1300px', margin: '0 auto' }}>

            {/* Header */}
            <Box display="flex" alignItems="center" gap={2} mb={3}>
                <IconButton onClick={onBack} sx={{ border: '1px solid #eee' }}>
                    <ArrowBackIcon />
                </IconButton>
                <Box>
                    <Typography variant="h5" fontWeight="bold" color="#002e4c">
                        Investment Applications
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                        Review, approve or reject investor investment applications
                    </Typography>
                </Box>
            </Box>

            {error && (
                <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
                    {error}
                </Alert>
            )}

            {/* Summary Cards */}
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} mb={4}>
                {[
                    { label: 'Total Applications', value: totalCount,    color: '#002e4c' },
                    { label: 'Pending Review',     value: pendingCount,  color: '#ed6c02' },
                    { label: 'Approved',           value: approvedCount, color: '#2e7d32' },
                    { label: 'Rejected',           value: rejectedCount, color: '#d32f2f' },
                ].map((card) => (
                    <Paper
                        key={card.label}
                        variant="outlined"
                        sx={{ p: 2.5, borderRadius: 3, flex: 1, textAlign: 'center' }}
                    >
                        <Typography variant="h4" fontWeight="900" color={card.color}>
                            {card.value}
                        </Typography>
                        <Typography variant="body2" color="textSecondary">
                            {card.label}
                        </Typography>
                    </Paper>
                ))}
            </Stack>

            {/* Table */}
            <TableContainer component={Paper} sx={{ borderRadius: 2, boxShadow: '0 4px 12px rgba(0,0,0,0.05)' }}>
                <Table>
                    <TableHead sx={{ bgcolor: '#f8f9fa' }}>
                        <TableRow>
                            <TableCell><strong>Investor</strong></TableCell>
                            <TableCell><strong>Fund Name</strong></TableCell>
                            <TableCell><strong>Amount</strong></TableCell>
                            <TableCell><strong>Date Applied</strong></TableCell>
                            <TableCell><strong>Notes</strong></TableCell>
                            <TableCell><strong>Status</strong></TableCell>
                            <TableCell align="center"><strong>Actions</strong></TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {loading ? (
                            <TableRow>
                                <TableCell colSpan={7} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                                    Loading applications...
                                </TableCell>
                            </TableRow>
                        ) : applications.length === 0 ? (
                            <TableRow>
                                <TableCell colSpan={7} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                                    No applications found.
                                </TableCell>
                            </TableRow>
                        ) : (
                            visibleApplications.map((app) => (
                                <TableRow key={app.applicationId} hover>
                                    <TableCell>
                                        <Typography variant="body2" fontWeight="600">{app.investorName}</Typography>
                                        <Typography variant="caption" color="textSecondary">ID: {app.investorId}</Typography>
                                    </TableCell>
                                    <TableCell>
                                        <Typography variant="body2" fontWeight="600">{app.fundName}</Typography>
                                        <Typography variant="caption" color="textSecondary">{app.fundVisibility}</Typography>
                                    </TableCell>
                                    <TableCell sx={{ fontWeight: 'bold', color: '#002e4c' }}>
                                        {formatCurrency(app.requestedCommitment)}
                                    </TableCell>
                                    <TableCell sx={{ fontSize: '0.85rem' }}>
                                        {formatDate(app.appliedAt)}
                                    </TableCell>
                                    <TableCell sx={{ maxWidth: 160 }}>
                                        <Typography
                                            variant="caption"
                                            color="textSecondary"
                                            sx={{
                                                display: '-webkit-box',
                                                WebkitLineClamp: 2,
                                                WebkitBoxOrient: 'vertical',
                                                overflow: 'hidden',
                                            }}
                                        >
                                            {app.notes}
                                        </Typography>
                                    </TableCell>
                                    <TableCell>
                                        <Chip
                                            label={STATUS_MAP[app.status]?.label || 'UNKNOWN'}
                                            color={STATUS_MAP[app.status]?.color || 'default'}
                                            size="small"
                                        />
                                    </TableCell>
                                    <TableCell align="center">
                                        {app.status === '3' ? (
                                            <Box display="flex" justifyContent="center" gap={1}>
                                                <Button
                                                    variant="contained"
                                                    color="success"
                                                    size="small"
                                                    onClick={() => openActionModal(app, 'accept')}
                                                >
                                                    Accept
                                                </Button>
                                                <Button
                                                    variant="outlined"
                                                    color="error"
                                                    size="small"
                                                    onClick={() => openActionModal(app, 'reject')}
                                                >
                                                    Reject
                                                </Button>
                                                <Button
                                                    variant="outlined"
                                                    size="small"
                                                    onClick={() => setSelectedApp(app)}
                                                >
                                                    View
                                                </Button>
                                            </Box>
                                        ) : (
                                            <Box display="flex" justifyContent="center" gap={1}>
                                                <Typography variant="caption" color="textSecondary">
                                                    Processed
                                                </Typography>
                                                <Button
                                                    variant="outlined"
                                                    size="small"
                                                    onClick={() => setSelectedApp(app)}
                                                >
                                                    View
                                                </Button>
                                            </Box>
                                        )}
                                    </TableCell>
                                </TableRow>
                            ))
                        )}
                    </TableBody>
                </Table>

                <TablePagination
                    rowsPerPageOptions={[5, 10, 25]}
                    component="div"
                    count={applications.length}
                    rowsPerPage={rowsPerPage}
                    page={page}
                    onPageChange={handleChangePage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                />
            </TableContainer>

            {/* ── NOTES / ACTION MODAL ── */}
            {actionModal && (
                <Dialog
                    open={!!actionModal}
                    onClose={handleCloseActionModal}
                    maxWidth="sm"
                    fullWidth
                >
                    <DialogTitle sx={{ 
                        fontWeight: 'bold',
                        color: actionModal.type === 'accept' ? '#2e7d32' : '#d32f2f'
                    }}>
                        {actionModal.type === 'accept' ? '✅ Approve Application' : '❌ Reject Application'}
                    </DialogTitle>

                    <DialogContent>
                        <Stack spacing={2} sx={{ pt: 1 }}>
                            {/* Application Summary */}
                            <Box sx={{ p: 2, bgcolor: '#f8f9fa', borderRadius: 2, border: '1px solid #eee' }}>
                                <Typography variant="caption" color="textSecondary">Investor</Typography>
                                <Typography variant="body2" fontWeight="600">
                                    {actionModal.app.investorName}
                                </Typography>
                                <Typography variant="caption" color="textSecondary">Fund</Typography>
                                <Typography variant="body2" fontWeight="600">
                                    {actionModal.app.fundName}
                                </Typography>
                                <Typography variant="caption" color="textSecondary">Amount</Typography>
                                <Typography variant="body2" fontWeight="600">
                                    {formatCurrency(actionModal.app.requestedCommitment)}
                                </Typography>
                            </Box>

                            {/* Notes input — required */}
                            <TextField
                                label={`${actionModal.type === 'accept' ? 'Approval' : 'Rejection'} Notes *`}
                                multiline
                                rows={4}
                                fullWidth
                                required
                                value={actionNote}
                                onChange={(e) => setActionNote(e.target.value)}
                                placeholder={
                                    actionModal.type === 'accept'
                                        ? 'e.g. Approved after verifying accreditation status...'
                                        : 'e.g. Rejected due to insufficient documentation...'
                                }
                                error={actionNote.trim() === ''}
                                helperText={actionNote.trim() === '' ? 'Notes are required' : ''}
                            />
                        </Stack>
                    </DialogContent>

                    <DialogActions sx={{ px: 3, pb: 2 }}>
                        <Button
                            variant="outlined"
                            onClick={handleCloseActionModal}
                            disabled={actionLoading}
                        >
                            Cancel
                        </Button>
                        <Button
                            variant="contained"
                            color={actionModal.type === 'accept' ? 'success' : 'error'}
                            disabled={!actionNote.trim() || actionLoading}
                            onClick={handleConfirmAction}
                        >
                            {actionLoading
                                ? 'Processing...'
                                : actionModal.type === 'accept' ? 'Confirm Approve' : 'Confirm Reject'
                            }
                        </Button>
                    </DialogActions>
                </Dialog>
            )}

            {/* Detail View Dialog */}
            {selectedApp && (
                <Dialog
                    open={!!selectedApp}
                    onClose={() => setSelectedApp(null)}
                    maxWidth="sm"
                    fullWidth
                >
                    <DialogTitle sx={{ fontWeight: 'bold', color: '#002e4c' }}>
                        Application #{selectedApp.applicationId}
                    </DialogTitle>
                    <DialogContent>
                        <Stack spacing={2} sx={{ pt: 1 }}>
                            {[
                                { label: 'Investor',   value: `${selectedApp.investorName} (ID: ${selectedApp.investorId})` },
                                { label: 'Fund',       value: `${selectedApp.fundName} (ID: ${selectedApp.fundId})` },
                                { label: 'Amount',     value: formatCurrency(selectedApp.requestedCommitment) },
                                { label: 'Applied On', value: formatDate(selectedApp.appliedAt) },
                                { label: 'Visibility', value: selectedApp.fundVisibility },
                                { label: 'Status',     value: STATUS_MAP[selectedApp.status]?.label || 'Unknown' },
                                { label: 'Notes',      value: selectedApp.notes },
                            ].map((row) => (
                                <Box key={row.label}>
                                    <Typography variant="caption" color="textSecondary">{row.label}</Typography>
                                    <Typography variant="body2" fontWeight="500">{row.value}</Typography>
                                    <Divider sx={{ mt: 1 }} />
                                </Box>
                            ))}
                        </Stack>
                    </DialogContent>
                    <DialogActions sx={{ px: 3, pb: 2 }}>
                        {selectedApp.status === '3' && (
                            <>
                                <Button
                                    variant="contained"
                                    color="error"
                                    onClick={() => openActionModal(selectedApp, 'reject')}
                                >
                                    Reject
                                </Button>
                                <Button
                                    variant="contained"
                                    color="success"
                                    onClick={() => openActionModal(selectedApp, 'accept')}
                                >
                                    Approve
                                </Button>
                            </>
                        )}
                        <Button variant="outlined" onClick={() => setSelectedApp(null)}>
                            Close
                        </Button>
                    </DialogActions>
                </Dialog>
            )}
        </Box>
    );
}

export default ApplicationsManager;